import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import FaqAccordion from "../../components/shared/FaqAccordion.jsx";
import { buildBreadcrumbJsonLd, buildEventJsonLd, buildFaqJsonLd } from "../../utils/jsonld.js";
import EventsMap, { MARKER_COLORS } from "../../components/events/EventsMap.jsx";
import ResponsiveImage from "../../components/shared/ResponsiveImage.jsx";
import { getEventsForLang } from "../../utils/content.js";
import { eventCategoryLabel } from "../../utils/eventCategories.js";
import { EVENTS_CONTENT } from "./eventsContent.js";

const LARP_PATH = { de: "larp-erleben", en: "experience-larp" };
const WAS_IST_LARP_SLUG = { de: "was-ist-larp", en: "what-is-larp" };
const JUGENDARBEIT_PATH = { de: "jugendarbeit", en: "youth-work" };

function formatEventDate(dateStr, lang) {
  return new Date(dateStr).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Own events (no externalOrga flag) attribute to Tales&Legends itself — everything else names the partner orga. */
function orgaLabel(f) {
  return f.externalOrga ? f.orgaName || "?" : "Tales&Legends";
}

/** Pill-shaped "Label ▾" trigger that opens a dropdown list of options — matches the Website.dc.html events filter bar (the "Tags ▾"/"Orga ▾" controls), not the old always-expanded pill row. */
function FilterDropdown({ label, allLabel, options, active, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(event) {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={label}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          color: active ? "var(--color-cream)" : "var(--color-cream-soft)",
          background: "none",
          border: `1px solid ${active ? "var(--color-gold)" : "rgba(254,250,239,0.18)"}`,
          padding: "8px 16px",
          borderRadius: "999px",
          cursor: "pointer",
          font: "inherit",
        }}
      >
        {active ?? label}
        <span style={{ color: "rgba(254,250,239,0.4)", fontSize: "10px" }}>▾</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, paddingTop: "8px", minWidth: "200px", zIndex: 30 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              background: "var(--color-bg-raised)",
              border: "1px solid rgba(254,250,239,0.12)",
              borderRadius: "12px",
              padding: "8px",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              maxHeight: "260px",
              overflowY: "auto",
            }}
          >
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="tl-nav-dropdown-item"
              style={{
                textAlign: "left",
                padding: "10px 14px",
                borderRadius: "8px",
                background: "none",
                border: "none",
                cursor: "pointer",
                font: "inherit",
                fontSize: "14px",
                color: active === null ? "var(--color-gold)" : "var(--color-cream)",
              }}
            >
              {allLabel ?? t("events.allTags")}
            </button>
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  onChange(option);
                  setOpen(false);
                }}
                aria-pressed={active === option}
                className="tl-nav-dropdown-item"
                style={{
                  textAlign: "left",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: "14px",
                  color: active === option ? "var(--color-gold)" : "var(--color-cream)",
                }}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EventCard({ event, lang, t }) {
  const f = event.frontmatter;
  return (
    <Link
      to={`/${lang}/events/${f.slug}`}
      className="tl-card-light tl-hover-lift"
      style={{ display: "flex", flexDirection: "column" }}
    >
      <ResponsiveImage
        src={f.image}
        alt={f.imageAlt || f.title}
        variant="light"
        style={{ width: "100%", height: "170px", objectFit: "cover" }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "24px", flex: 1 }}>
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          <span className="tl-badge">{formatEventDate(f.date, lang)}</span>
          <span
            style={{
              fontSize: "11px",
              color: f.externalOrga ? "rgba(10,9,7,0.6)" : "#8a6520",
              border: `1px solid ${f.externalOrga ? "rgba(10,9,7,0.18)" : "rgba(206,149,50,0.5)"}`,
              padding: "3px 9px",
              borderRadius: "999px",
            }}
          >
            {orgaLabel(f)}
          </span>
          <span style={{ fontSize: "12px", color: "rgba(10,9,7,0.7)" }}>{f.location}</span>
        </div>
        <span style={{ fontFamily: "var(--font-serif)", fontSize: "22px", lineHeight: 1.22, color: "var(--color-ink)" }}>
          {f.title}
        </span>
        {f.tags?.length > 0 && (
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {f.tags.map((tag) => (
              <span key={tag} style={{ fontSize: "11px", color: "rgba(10,9,7,0.6)", border: "1px solid rgba(10,9,7,0.18)", padding: "3px 9px", borderRadius: "999px" }}>
                {tag}
              </span>
            ))}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto" }}>
          <span style={{ fontSize: "13px", color: "rgba(10,9,7,0.7)" }}>
            {`${t("events.priceFrom")} ${f.price} €`}
          </span>
          <span className="tl-pill tl-pill-dark" style={{ fontSize: "13px" }}>
            {t("events.detailsCta")}
          </span>
        </div>
      </div>
    </Link>
  );
}

function EventGrid({ title, events, lang, t }) {
  if (events.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "24px", color: "var(--color-cream)", margin: 0 }}>{title}</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: "24px" }}>
        {events.map((event) => (
          <EventCard key={event.frontmatter.slug} event={event} lang={lang} t={t} />
        ))}
      </div>
    </div>
  );
}

function MapLegend({ t }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        flex: "0 0 200px",
        background: "var(--color-bg-raised)",
        border: "1px solid rgba(254,250,239,0.12)",
        borderRadius: "12px",
        padding: "18px 20px",
      }}
    >
      <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "14px", color: "var(--color-gold)" }}>
        {t("events.mapLegendTitle")}
      </span>
      {[
        { color: MARKER_COLORS.own, label: t("events.mapLegendOwn") },
        { color: MARKER_COLORS.external, label: t("events.externalOrgaBadge") },
      ].map(({ color, label }) => (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ width: "12px", height: "12px", borderRadius: "999px", background: color, flex: "none" }} />
          <span style={{ fontSize: "13px", color: "var(--color-cream-soft)" }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

export default function EventList({ lang }) {
  const { t } = useTranslation();
  const [view, setView] = useState("list");
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [activeOrga, setActiveOrga] = useState(null);
  const events = getEventsForLang(lang).sort((a, b) => a.frontmatter.date.localeCompare(b.frontmatter.date));
  const allCategories = [...new Set(events.map((e) => e.frontmatter.category).filter(Boolean))];
  const allTags = [...new Set(events.flatMap((e) => e.frontmatter.tags ?? []))].sort();
  const allOrgas = [...new Set(events.map((e) => orgaLabel(e.frontmatter)))].sort(
    (a, b) => (b === "Tales&Legends") - (a === "Tales&Legends") || a.localeCompare(b)
  );
  const visibleEvents = events
    .filter((e) => !activeCategory || e.frontmatter.category === activeCategory)
    .filter((e) => !activeTag || (e.frontmatter.tags ?? []).includes(activeTag))
    .filter((e) => !activeOrga || orgaLabel(e.frontmatter) === activeOrga);
  // Plain string comparison on the YYYY-MM-DD frontmatter fields, not Date
  // objects — avoids any timezone subtlety between the prerender snapshot
  // and the client's own render, which only matters here because an event
  // landing in a different group at each pass would itself be a hydration
  // mismatch (two different lists of cards), not just a display quirk.
  const today = new Date().toISOString().slice(0, 10);
  const isPast = (e) => (e.frontmatter.endDate || e.frontmatter.date) < today;
  // `events` is already sorted ascending by date, so both groups inherit
  // that order via filter — reversed for past events so the most recent
  // one leads, matching how upcoming events lead with the soonest one.
  const upcomingEvents = visibleEvents.filter((e) => !isPast(e));
  const pastEvents = visibleEvents.filter(isPast).reverse();
  // Map view is upcoming-only — a pin for an event that already happened
  // isn't useful for "where can I go," and keeping past events out of the
  // set entirely (not just visually deprioritized) also keeps the map's
  // own guard below meaningful once an org has a long event history.
  // Guards the actual Maps API load, not just the toggle button — a
  // billable "map load" must never fire for an empty map (see
  // konzepte/GOOGLE_MAPS_SETUP.md).
  const mappableEvents = upcomingEvents.filter((e) => e.frontmatter.coordinates?.length === 2);

  return (
    <>
      <SEOHead lang={lang} path="/events" title={t("events.metaTitle")} description={t("events.description")} />
      <JsonLd data={buildFaqJsonLd(EVENTS_CONTENT[lang].faq.items.map(({ q, a }) => ({ question: q, answer: a })))} />
      <JsonLd
        data={buildBreadcrumbJsonLd(
          [
            { name: t("nav.home"), path: "/" },
            { name: t("nav.events"), path: "/events" },
          ],
          lang
        )}
      />
      {visibleEvents.map((event) => (
        <JsonLd key={event.frontmatter.slug} data={buildEventJsonLd(event.frontmatter, lang)} />
      ))}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "32px", paddingTop: "64px", paddingBottom: "96px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", maxWidth: "52ch" }}>
            <p className="tl-eyebrow" style={{ margin: 0 }}>
              {t("events.eyebrow")}
            </p>
            <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", margin: 0 }}>
              {t("events.title")}
            </h1>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            {allCategories.length > 0 && (
              <div role="group" aria-label={t("events.filterByCategory")} style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => setActiveCategory(null)}
                  className={`tl-pill ${activeCategory === null ? "tl-pill-primary" : "tl-pill-outline"}`}
                  style={{ padding: "9px 18px", fontSize: "13px" }}
                >
                  {t("events.allTags")}
                </button>
                {allCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setActiveCategory(category)}
                    aria-pressed={activeCategory === category}
                    className={`tl-pill ${activeCategory === category ? "tl-pill-primary" : "tl-pill-outline"}`}
                    style={{ padding: "9px 18px", fontSize: "13px" }}
                  >
                    {eventCategoryLabel(category, lang)}
                  </button>
                ))}
                <span style={{ width: "1px", height: "22px", background: "rgba(254,250,239,0.14)", margin: "0 6px" }} />
              </div>
            )}
            {allOrgas.length > 1 && (
              <FilterDropdown label={t("events.filterByOrga")} options={allOrgas} active={activeOrga} onChange={setActiveOrga} />
            )}
            {allTags.length > 0 && (
              <FilterDropdown label={t("events.filterByTag")} options={allTags} active={activeTag} onChange={setActiveTag} />
            )}
            </div>

            <button
              type="button"
              onClick={() => setView((v) => (v === "list" ? "map" : "list"))}
              aria-label={t("events.viewToggleLabel")}
              className="tl-pill tl-pill-outline"
              style={{ padding: "10px 18px", fontSize: "13px", flex: "none" }}
            >
              {view === "list" ? t("events.viewMap") : t("events.viewList")}
            </button>
          </div>

          {events.length === 0 && <p style={{ color: "var(--color-cream-soft)" }}>{t("events.noEvents")}</p>}
          {events.length > 0 && visibleEvents.length === 0 && (
            <p style={{ color: "var(--color-cream-soft)" }}>{t("events.noFilteredEvents")}</p>
          )}

          {view === "map" && visibleEvents.length > 0 && mappableEvents.length > 0 && (
            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 480px" }}>
                <EventsMap events={mappableEvents} lang={lang} />
              </div>
              <MapLegend t={t} />
            </div>
          )}
          {view === "map" && visibleEvents.length > 0 && mappableEvents.length === 0 && (
            <p style={{ color: "var(--color-cream-soft)" }}>{t("events.mapNoLocations")}</p>
          )}

          {view === "list" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "40px" }}>
              {upcomingEvents.length > 0 ? (
                <EventGrid title={t("events.upcomingTitle")} events={upcomingEvents} lang={lang} t={t} />
              ) : (
                // Only shown when there's at least one visible event elsewhere (past, or a
                // different filter result) — a fully empty page already gets noEvents/
                // noFilteredEvents above, and this would otherwise duplicate that message.
                visibleEvents.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                    <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "24px", color: "var(--color-cream)", margin: 0 }}>
                      {t("events.upcomingTitle")}
                    </h2>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "14px",
                        background: "var(--color-bg-raised)",
                        border: "1px solid rgba(254,250,239,0.12)",
                        borderRadius: "12px",
                        padding: "24px 28px",
                      }}
                    >
                      <p style={{ color: "var(--color-cream-soft)", margin: 0 }}>{EVENTS_CONTENT[lang].planning.intro}</p>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px", flexWrap: "wrap", width: "100%" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <span style={{ fontSize: "12px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--color-cream-faint)" }}>
                            {EVENTS_CONTENT[lang].planning.title}
                          </span>
                          {EVENTS_CONTENT[lang].planning.items.map((item) => (
                            <p key={item} style={{ fontSize: "14px", color: "var(--color-cream-soft)", margin: 0 }}>
                              {item}
                            </p>
                          ))}
                        </div>
                        <Link to={`/${lang}/newsletter`} className="tl-pill tl-pill-primary" style={{ flex: "none" }}>
                          {EVENTS_CONTENT[lang].planning.newsletterCta}
                        </Link>
                      </div>
                    </div>
                  </div>
                )
              )}
              <EventGrid title={t("events.pastTitle")} events={pastEvents} lang={lang} t={t} />
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "24px", borderTop: "1px solid rgba(254,250,239,0.1)", paddingTop: "40px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
                {EVENTS_CONTENT[lang].formats.title}
              </h2>
              <p style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0, maxWidth: "64ch" }}>
                {EVENTS_CONTENT[lang].formats.intro}
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: "20px" }}>
              {EVENTS_CONTENT[lang].formats.items.map((format) => (
                <div key={format.title} className="tl-card" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <span style={{ fontFamily: "var(--font-serif)", fontSize: "19px", color: "var(--color-cream)" }}>{format.title}</span>
                  <span style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-soft)" }}>{format.body}</span>
                  {format.link && (
                    <Link to={`/${lang}/${JUGENDARBEIT_PATH[lang]}`} style={{ fontSize: "13px", color: "var(--color-gold)", alignSelf: "flex-start" }}>
                      {lang === "de" ? "Zur Seite Jugendarbeit →" : "Youth work page →"}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: "40px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
                {EVENTS_CONTENT[lang].locations.title}
              </h2>
              <p style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>
                {EVENTS_CONTENT[lang].locations.body}
                {" "}
                {EVENTS_CONTENT[lang].locations.communityPrefix}{" "}
                <a href="https://discord.gg/6Xw4fycXBc" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-gold)" }}>
                  {lang === "de" ? "Community" : "community"}
                </a>{" "}
                {EVENTS_CONTENT[lang].locations.communitySuffix}
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
                {EVENTS_CONTENT[lang].beginners.title}
              </h2>
              <p style={{ fontSize: "15px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>
                {EVENTS_CONTENT[lang].beginners.body}
                {" "}
                <Link to={`/${lang}/${LARP_PATH[lang]}/${WAS_IST_LARP_SLUG[lang]}`} style={{ color: "var(--color-gold)" }}>
                  {EVENTS_CONTENT[lang].beginners.links[0].label}
                </Link>
                {lang === "de" ? " und in unserer " : " and in "}
                <Link to={`/${lang}/${LARP_PATH[lang]}/faq`} style={{ color: "var(--color-gold)" }}>
                  {EVENTS_CONTENT[lang].beginners.links[1].label}
                </Link>
                .
              </p>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "20px", borderTop: "1px solid rgba(254,250,239,0.1)", paddingTop: "40px" }}>
            <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "26px", color: "var(--color-cream)", margin: 0 }}>
              {EVENTS_CONTENT[lang].faq.title}
            </h2>
            <FaqAccordion items={EVENTS_CONTENT[lang].faq.items.map(({ q, a, links }) => ({ question: q, answer: a, links }))} />
          </div>
        </div>
      </div>
    </>
  );
}
